import React, { useState, useEffect } from 'react';
import { useApi } from './DashboardLayout';
import { Shield, Download, RefreshCw, AlertTriangle, CheckCircle, Copy } from 'lucide-react';

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label className="of-label">{label}</label>
    {children}
  </div>
);

const TF = ({ label, value, onChange, type = 'text', disabled }) => (
  <Field label={label}>
    <input className="of-input" type={type} value={value} onChange={onChange}
      style={disabled ? { background: 'var(--page-bg)', cursor: 'not-allowed' } : {}}
      disabled={disabled} />
  </Field>
);

export default function ProfilePage({ user, setUser }) {
  const [form, setForm] = useState({ ik_number: '', verein: '', street: '', postal_code: '', city_location: '', phone: '', contact_person: '', email: '', website: '', dachverband: '', billing_office_name: '', billing_office_ik_number: '' });
  const [pwForm, setPwForm] = useState({ new_password: '' });
  const [msg, setMsg] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [twofaStatus, setTwofaStatus] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [twofaMsg, setTwofaMsg] = useState('');
  const [twofaLoading, setTwofaLoading] = useState(false);
  const [copyMsg, setCopyMsg] = useState('');
  const { apiFetch } = useApi();
  const userType = parseInt(localStorage.getItem('bgpr_user_type') || '0');

  React.useEffect(() => {
    if (user) setForm(prev => ({
      ...prev,
      ik_number: user.ik_number || '', verein: user.verein || '', street: user.street || '',
      postal_code: user.postal_code || '', city_location: user.city_location || '',
      phone: user.phone || '', contact_person: user.contact_person || '',
      email: user.email || '', website: user.website || '', dachverband: user.dachverband || '',
      billing_office_name: user.billing_office_name || '', billing_office_ik_number: user.billing_office_ik_number || ''
    }));
  }, [user]);

  useEffect(() => {
    apiFetch('/api/profile/2fa/status').then(r => r?.json()).then(d => { if (d) setTwofaStatus(d); });
    if (window.location.hash === '#2fa') {
      setTimeout(() => document.getElementById('2fa-section')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, []);

  const handleSave = async (e) => {
    e.preventDefault(); setMsg('');
    const data = { ...form };
    if (data.website && !data.website.match(/^https?:\/\//)) {
      data.website = 'https://' + data.website;
      setForm(p => ({ ...p, website: data.website }));
    }
    const r = await apiFetch('/api/profile', { method: 'PUT', body: JSON.stringify(data) });
    if (r?.ok) { setMsg('Profil gespeichert'); const mr = await apiFetch('/api/auth/me'); const md = await mr?.json(); if (md) setUser(md); }
    else setMsg('Fehler beim Speichern');
  };

  const handlePwChange = async (e) => {
    e.preventDefault(); setPwMsg('');
    if (pwForm.new_password.length < 8) { setPwMsg('Mindestens 8 Zeichen'); return; }
    const r = await apiFetch('/api/profile/change-password', { method: 'POST', body: JSON.stringify(pwForm) });
    if (r?.ok) { setPwMsg('Passwort geändert'); setPwForm({ new_password: '' }); } else setPwMsg('Fehler');
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleToggle2fa = async () => {
    setTwofaLoading(true); setTwofaMsg('');
    const newState = !twofaStatus?.twofa_enabled;
    const r = await apiFetch('/api/profile/2fa/toggle', { method: 'POST', body: JSON.stringify({ enabled: newState }) });
    const d = await r?.json();
    if (d) { setTwofaMsg(d.message); setTwofaStatus(prev => ({ ...prev, twofa_enabled: d.twofa_enabled, ...(newState ? {} : { has_backup_codes: false, backup_codes_remaining: 0 }) })); setBackupCodes(null); }
    setTwofaLoading(false);
  };

  const handleGenerateCodes = async () => {
    setTwofaLoading(true); setTwofaMsg('');
    const r = await apiFetch('/api/profile/2fa/backup-codes', { method: 'POST' });
    const d = await r?.json();
    if (d?.codes) { setBackupCodes(d.codes); setTwofaMsg(d.message); setTwofaStatus(prev => ({ ...prev, has_backup_codes: true, backup_codes_remaining: d.codes.length })); }
    else setTwofaMsg('Fehler beim Generieren der Codes');
    setTwofaLoading(false);
  };

  const handleDownloadCodes = () => {
    if (!backupCodes) return;
    const text = 'BGPR Portal — 2FA Notfallcodes\n' + '='.repeat(40) + '\nGeneriert am: ' + new Date().toLocaleString('de-DE') + '\n\nBitte sicher aufbewahren!\n\n' + backupCodes.map((c, i) => `${(i + 1).toString().padStart(2, '0')}. ${c}`).join('\n') + '\n\nJeder Code kann nur einmal verwendet werden.\n';
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'bgpr-notfallcodes.txt'; a.click();
  };
  if (!user) return null;

  const isProvider = userType === 3;
  const Feedback = ({ text }) => {
    if (!text) return null;
    const isError = text.includes('Fehler') || text.includes('Mind');
    return (
      <div style={{ padding: '8px 12px', borderRadius: 8, background: isError ? '#FEE2E2' : '#D1FAE5', color: isError ? '#991B1B' : '#065F46', marginBottom: 12, fontSize: 12, fontWeight: 500 }}>{text}</div>
    );
  };

  return (
    <div data-testid="profile-page">
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Profil</h2>
        <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>Persönliche Einstellungen</p>
      </div>

      <form onSubmit={handleSave}>
        <Feedback text={msg} />

        {isProvider ? (
          <>
            {/* Provider: Two column layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="of-card">
                <div className="of-section">Vereinsdaten</div>
                <TF label="IK-Nummer" value={form.ik_number} onChange={e => set('ik_number', e.target.value)} />
                <TF label="Verein" value={form.verein} onChange={e => set('verein', e.target.value)} />
                <TF label="Dachverband" value={form.dachverband} onChange={e => set('dachverband', e.target.value)} />
              </div>
              <div className="of-card">
                <div className="of-section">Kontaktdaten</div>
                <TF label="Ansprechpartner" value={form.contact_person} onChange={e => set('contact_person', e.target.value)} />
                <TF label="E-Mail" value={form.email} onChange={e => set('email', e.target.value)} type="email" />
                <div className="of-2col">
                  <TF label="Telefon" value={form.phone} onChange={e => set('phone', e.target.value)} />
                  <TF label="Website" value={form.website} onChange={e => set('website', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Full width: Adresse & Abrechnung */}
            <div className="of-card" style={{ marginBottom: 16 }}>
              <div className="of-section">Adresse & Abrechnung</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 16px' }}>
                <TF label="Straße" value={form.street} onChange={e => set('street', e.target.value)} />
                <TF label="PLZ" value={form.postal_code} onChange={e => set('postal_code', e.target.value)} />
                <TF label="Ort" value={form.city_location} onChange={e => set('city_location', e.target.value)} />
              </div>
              <div className="of-2col">
                <TF label="Abrechnungsstelle" value={form.billing_office_name} onChange={e => set('billing_office_name', e.target.value)} />
                <TF label="Abrechnungsstelle IK" value={form.billing_office_ik_number} onChange={e => set('billing_office_ik_number', e.target.value)} />
              </div>
            </div>
          </>
        ) : (
          /* Admin: simple email card */
          <div className="of-card" style={{ marginBottom: 16 }}>
            <div className="of-section">Kontodaten</div>
            <TF label="E-Mail" value={form.email} onChange={e => set('email', e.target.value)} type="email" />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          <button className="btn btn-primary" type="submit" data-testid="profile-save-btn" style={{ padding: '8px 24px' }}>Aktualisieren</button>
        </div>
      </form>

      {/* Passwort ändern */}
      <div className="of-card">
        <div className="of-section">Passwort ändern</div>
        <Feedback text={pwMsg} />
        <form onSubmit={handlePwChange} style={{ maxWidth: 400 }}>
          <TF label="Neues Passwort" value={pwForm.new_password} onChange={e => setPwForm({ new_password: e.target.value })} type="password" />
          <button className="btn btn-primary" type="submit" data-testid="profile-pw-btn" style={{ padding: '8px 24px' }}>Passwort ändern</button>
        </form>
      </div>

      {/* 2FA Verwaltung */}
      <div className="of-card" id="2fa-section" style={{ marginTop: 16 }} data-testid="2fa-section">
        <div className="of-section" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Shield size={16} /> Zwei-Faktor-Authentifizierung (2FA)</div>

        {twofaStatus && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 8, background: twofaStatus.twofa_enabled ? '#D1FAE5' : '#FEF3C7', marginBottom: 12 }}>
              {twofaStatus.twofa_enabled
                ? <><CheckCircle size={18} color="#059669" /><span style={{ fontSize: 13, color: '#065F46', fontWeight: 600 }}>2FA ist aktiviert</span></>
                : <><AlertTriangle size={18} color="#D97706" /><span style={{ fontSize: 13, color: '#92400E', fontWeight: 600 }}>2FA ist deaktiviert</span></>}
            </div>

            <button className={twofaStatus.twofa_enabled ? 'btn' : 'btn btn-primary'} onClick={handleToggle2fa} disabled={twofaLoading}
              data-testid="2fa-toggle-btn"
              style={twofaStatus.twofa_enabled ? { padding: '8px 20px', color: '#DC2626', border: '1px solid #FCA5A5', background: '#FEF2F2' } : { padding: '8px 20px' }}>
              {twofaStatus.twofa_enabled ? '2FA deaktivieren' : '2FA aktivieren'}
            </button>
          </div>
        )}

        {twofaStatus?.twofa_enabled && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16 }}>
            <h4 style={{ fontSize: 14, margin: '0 0 6px', color: 'var(--text-primary)' }}>Notfallcodes</h4>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Notfallcodes ermöglichen den Login, falls Sie keinen Zugriff auf Ihren Authenticator haben. Jeder Code ist nur einmal verwendbar.
              {twofaStatus.has_backup_codes && <><br /><strong>{twofaStatus.backup_codes_remaining} Code(s) verbleibend.</strong></>}
            </p>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <button className="btn btn-primary" onClick={handleGenerateCodes} disabled={twofaLoading}
                data-testid="2fa-generate-codes-btn"
                style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={14} /> {twofaStatus.has_backup_codes ? 'Neue Codes generieren' : 'Notfallcodes generieren'}
              </button>
              {backupCodes && (
                <button className="btn" onClick={handleDownloadCodes}
                  data-testid="2fa-download-codes-btn"
                  style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #ddd' }}>
                  <Download size={14} /> Als Datei speichern
                </button>
              )}
              {backupCodes && (
                <button className="btn" onClick={() => {
                  const text = backupCodes.map((c, i) => `${String(i + 1).padStart(2, '0')}. ${c}`).join('\n');
                  navigator.clipboard.writeText(text).then(() => {
                    setCopyMsg('Kopiert!'); setTimeout(() => setCopyMsg(''), 2000);
                  });
                }}
                  data-testid="2fa-copy-codes-btn"
                  style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #ddd' }}>
                  <Copy size={14} /> {copyMsg || 'Codes kopieren'}
                </button>
              )}
            </div>

            {backupCodes && (
              <div data-testid="2fa-codes-display" style={{ background: '#1e293b', borderRadius: 8, padding: 16 }}>
                <p style={{ color: '#F59E0B', fontSize: 12, fontWeight: 600, margin: '0 0 10px' }}>Bitte sicher aufbewahren — diese Codes werden nur einmal angezeigt!</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 24px' }}>
                  {backupCodes.map((code, i) => (
                    <span key={i} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: '#e2e8f0', padding: '3px 0' }}>
                      {String(i + 1).padStart(2, '0')}. {code}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {twofaMsg && <Feedback text={twofaMsg} />}
          </div>
        )}
        {twofaMsg && !twofaStatus?.twofa_enabled && <Feedback text={twofaMsg} />}
      </div>
    </div>
  );
}
